import { useCallback, useEffect, useState } from "react";
import { ApiError, getApi, ZOD_PARSE_ERROR } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { API_OFFBOARDING_DETAIL } from "../../../../../config/Endpoints.js";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { sqeOffboardingApi } from "../api/sqe-offboarding-api.ts";
import {
    OffboardingDetail,
    OffboardingDetailSchema,
} from "../schemas/index.ts";

export function useOffboardingDetail(offboardingId: number | null) {
    const [detail, setDetail] = useState<OffboardingDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isSqe = useSqeLabs();

    const fetchDetail = useCallback(
        async (signal?: AbortSignal) => {
            if (offboardingId == null) {
                setDetail(null);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                if (isSqe) {
                    setDetail(await sqeOffboardingApi.getDetail(offboardingId, signal));
                    return;
                }
                const url = `${API_OFFBOARDING_DETAIL}/${offboardingId}`;
                const { data } = await getApi<OffboardingDetail>(
                    url,
                    accessToken(),
                    "json",
                    signal,
                );

                if (!data) {
                    setDetail(null);
                } else {
                    const parse = OffboardingDetailSchema.safeParse(data);

                    if (!parse.success) {
                        setDetail(null);
                        setError(ZOD_PARSE_ERROR);
                    } else {
                        setDetail(parse.data);
                    }
                }
            } catch (e) {
                const msg =
                    e instanceof ApiError
                        ? e.message
                        : "Failed to load offboarding detail.";
                setError(msg);
                setDetail(null);
            } finally {
                setLoading(false);
            }
        },
        [offboardingId, isSqe],
    );

    useEffect(() => {
        const ac = new AbortController();
        void fetchDetail(ac.signal);
        return () => ac.abort();
    }, [fetchDetail]);

    return { detail, loading, error, refetch: fetchDetail };
}
